import { Type } from '@angular/core';
import { Data, ResolveData, Route, Routes } from '@angular/router';
import { pageAuthorize } from '../guards/page-authorize.guard';
import { pageSettings } from '../models/core/security/page-settings';
import { PageToken } from '../models/core/security/page-token';
import { AlertEditPageComponent } from './alerts/alert-edit/alert-edit-page.component';
import { AlertsLandingComponent } from './alerts/alerts-landing.component';
import { AlertsLayoutComponent } from './alerts/alerts-layout.component';
import { ApplicationsPageComponent } from './applications/applications-page.component';
import { EmptyComponent } from './core/labels/empty.component';
import { DashboardsLayoutComponent } from './dashboards/dashboards-layout.component';
import { DesignPageComponent } from './design/design-page.component';
import { DownloadsViewComponent } from './downloads/downloads-view.component';
import { ExternalDistributedReportsLayoutComponent } from './external-distributed-reports/external-distributed-reports-layout.component';
import { ExternalDistributedReportsPageComponent } from './external-distributed-reports/external-distributed-reports-page.component';
import { JurisSidekickSsoLayoutComponent } from './juris-sidekick-sso/juris-sidekick-sso-layout.component';
import { JurisSidekickSsoPageComponent } from './juris-sidekick-sso/juris-sidekick-sso-page.component';
import { HomePageComponent } from './home/home-page.component';
import { LayoutComponent } from './layout/layout.component';
import { PageLayoutComponent } from './layout/page-layout.component';
import { ForbiddenPageComponent } from './layout/pages/forbidden-page.component';
import { NotFoundPageComponent } from './layout/pages/not-found-page.component';
import { AccountLockedComponent } from './login/account-locked.component';
import { ForgotPasswordComponent } from './login/forgot-password.component';
import { ForgotUsernameComponent } from './login/forgot-username.component';
import { LoginLayoutComponent } from './login/login-layout.component';
import { LoginComponent } from './login/login.component';
import { MfaComponent } from './login/mfa.component';
import { OtpComponent } from './login/otp.component';
import { PasswordResetComponent } from './login/password-reset.component';
import { CommunicationPreferencesComponent } from './preferences/communication-preferences.component';
import { PasswordChangeComponent } from './preferences/password-change.component';
import { PreferencesComponent } from './preferences/preference.component';
import { QaToolsComponent } from './qa-tools/qa-tools.component';
import { QueryLayoutComponent } from './query/query-layout.component';
import { QueryPageComponent } from './query/query-page.component';
import { CompletedReportPageComponent } from './reports/completed-report/completed-report-page.component';
import { ReportsLayoutComponent } from './reports/core/reports-layout.component';
import { DistributedReportPageComponent } from './reports/distributed-report/distributed-report-page.component';
import { EditFilterPageComponent } from './reports/edit-filter/edit-filter-page.component';
import { EditReportPageComponent } from './reports/edit-report/edit-report-page.component';
import { EditScheduleComponent } from './reports/edit-report/edit-schedule-page.component';
import { MyReportPageComponent } from './reports/my-report/my-report-page.component';
import { PendingReportPageComponent } from './reports/pending-report/pending-report-page.component';
import { ReportsLandingComponent } from './reports/report-landing/reports-landing.component';
import { ScheduledReportPageComponent } from './reports/scheduled-report/scheduled-report-page.component';
import { SsoErrorPageComponent } from './sso/sso-error-page.component';
import { SsoLayoutComponent } from './sso/sso-layout.component';
import { SsoLoggedOutPageComponent } from './sso/sso-logged-out-page.component';
import { SsoUnauthorizedPageComponent } from './sso/sso-unauthorized-page.component';
import { DiaryEditPageComponent } from './todo/client-diaries/diary-edit-page.component';
import { DiaryTypesPageComponent } from './todo/client-diaries/diary-types-page.component';
import { TaskEditPageComponent } from './todo/personal-tasks/task-edit-page.component';
import { TodoViewComponent } from './todo/todo-view.component';
import { ClaimSummaryComponent } from './view/claim-summary/claim-summary.component';
import { SidekickSummaryComponent } from './view/claim-summary/sidekick-summary/sidekick-summary.component';
import { AccommodationsComponent } from './view/details/accommodations/accommodations.component';
import { InteractionsComponent } from './view/details/accommodations/interactions.component';
import { JobFunctionsComponent } from './view/details/accommodations/job-functions.component';
import { MilestonesComponent } from './view/details/accommodations/milestones.component';
import { RestrictionsComponent } from './view/details/accommodations/restrictions.component';
import { AddressBookComponent } from './view/details/address-book/address-book.component';
import { AppealsComponent } from './view/details/appeals/appeals.component';
import { BenefitPlanComponent } from './view/details/benefit-plan/benefit-plan.component';
import { CaseManagementDetailComponent } from './view/details/case-management/case-management-detail.component';
import { CaseManagementComponent } from './view/details/case-management/case-management.component';
import { CertificationsComponent } from './view/details/certifications/certifications.component';
import { ClaimantComponent } from './view/details/claimant/claimant.component';
import { CustomFieldsPageComponent } from './view/details/custom-fields/custom-fields-page.component';
import { AbsenceComponent } from './view/details/detail/absence/absence.component';
import { AutoComponent } from './view/details/detail/auto/auto.component';
import { DisabilityComponent } from './view/details/detail/disability/disability.component';
import { GeneralLiabilityComponent } from './view/details/detail/general-liability/general-liability.component';
import { PropertyComponent } from './view/details/detail/property/property.component';
import { WorkersCompensationComponent } from './view/details/detail/workers-compensation/workers-compensation.component';
import { ElectronicDataInterchangeComponent } from './view/details/electronic-data-interchange/electronic-data-interchange.component';
import { EligibilityComponent } from './view/details/eligibility/eligibility.component';
import { LeaveCalendarPageComponent } from './view/details/leave-calendar/leave-calendar-page.component';
import { LeaveCalendarResolver } from './view/details/leave-calendar/leave-calendar.resolver';
import { LegalComponent } from './view/details/legal/legal.component';
import { MedicalComponent } from './view/details/medical/medical.component';
import { MedicareComponent } from './view/details/medicare/medicare.component';
import { PlanSummaryPageComponent } from './view/details/plan-summary/plan-summary-page.component';
import { PolicyDetailPageComponent } from './view/details/policy/policy-detail-page.component';
import { TenderComponent } from './view/details/tender/tender.component';
import { TimeTrackingComponent } from './view/details/time-tracking/time-tracking.component';
import { VendorReferralComponent } from './view/details/vendor-referrals/vendor-referral.component';
import { CorrespondenceComponent } from './view/documentation/correspondence/correspondence.component';
import { DocumentsComponent } from './view/documentation/documents/documents.component';
import { UploadDocumentsComponent } from './view/documentation/documents/upload-documents';
import { ExaminerDiaryComponent } from './view/documentation/examiner-diary/examiner-diary.component';
import { IndexSystemPageComponent } from './view/documentation/index-system/index-system-page.component';
import { NoteEditPageComponent } from './view/documentation/notes/note-edit-page.component';
import { NotesComponent } from './view/documentation/notes/notes.component';
import { ServiceInstructionComponent } from './view/documentation/service-instructions/service-instructions.component';
import { InvoiceBillComponent } from './view/financials/invoice/invoice-bill.component';
import { InvoiceListComponent } from './view/financials/invoice/invoice-list.component';
import { OffsetsComponent } from './view/financials/offsets/offsets.component';
import { OverpaymentDetailComponent } from './view/financials/overpayments/overpayment-detail.component';
import { OverpaymentsComponent } from './view/financials/overpayments/overpayments.component';
import { OverviewComponent } from './view/financials/overview/overview.component';
import { PaymentHistoryComponent } from './view/financials/payment-history/payment-history.component';
import { RecoveriesComponent } from './view/financials/recoveries/recoveries.component';
import { ReimbursementsComponent } from './view/financials/reimbursements/reimbursements.component';
import { ReservesComponent } from './view/financials/reserves/reserves.component';
import { AccrualsComponent } from './view/hr/accruals/accruals.component';
import { DeductionsComponent } from './view/hr/deductions/deductions.component';
import { DependentsComponent } from './view/hr/dependents/dependents.component';
import { EarningsComponent } from './view/hr/earnings/earnings.component';
import { EmployeeDetailComponent } from './view/hr/employee-detail/employee-detail.component';
import { HrContactsComponent } from './view/hr/hr-contacts/hr-contacts.component';
import { JobHistoryComponent } from './view/hr/job-history/job-history.component';
import { PayrollComponent } from './view/hr/payroll/payroll.component';
import { ViewLandingComponent } from './view/landing/view-landing.component';
import { ClaimLayoutComponent } from './view/layout/claim-layout.component';
import { ClaimResolver } from './view/layout/claim.resolver';
import { MyTasksComponent } from './view/my-tasks/my-tasks.component';

const prefix = 'OmniInsights - ';

export const routes: Routes = [
  {
    path: 'login',
    component: LoginLayoutComponent,
    children: [
      add('login-page', LoginComponent),
      add('forgot-password-page', ForgotPasswordComponent),
      add('forgot-username-page', ForgotUsernameComponent),
      add('mfa-page', MfaComponent),
      add('password-reset-page', PasswordResetComponent),
      add('account-locked-page', AccountLockedComponent),
      add('otp-page', OtpComponent),
    ],
  },
  {
    path: 'sso',
    component: SsoLayoutComponent,
    children: [
      {
        path: 'logged-out',
        component: SsoLoggedOutPageComponent,
      },
      {
        path: 'unauthorized',
        component: SsoUnauthorizedPageComponent,
      },
      {
        path: 'error',
        component: SsoErrorPageComponent,
      },
    ],
  },
  {
    path: 'external-distributed-reports',
    component: ExternalDistributedReportsLayoutComponent,
    canActivate: [],
    children: [
      {
        path: '',
        component: ExternalDistributedReportsPageComponent,
      },
      {
        path: 'distributed-report/:reportId',
        component: DistributedReportPageComponent,
        data: {
          externalAccess: true,
        },
      },
    ],
  },
  {
    path: 'juris-sidekick-sso/:recordSource/:recordId',
    component: JurisSidekickSsoLayoutComponent,
    canActivate: [],
    children: [
      {
        path: '',
        component: JurisSidekickSsoPageComponent,
      },
    ],
  },
  {
    path: '',
    component: LayoutComponent,
    children: [
      {
        path: 'reports',
        component: ReportsLayoutComponent,
        canActivate: [pageAuthorize('reports-page')],
        children: [
          add('reports-landing-page', ReportsLandingComponent, {
            routeCacheKey: 'ReportsLandingComponent',
          }),
          add('completed-report-page', CompletedReportPageComponent),
          add('distributed-report-page', DistributedReportPageComponent),
          add('scheduled-report-page', ScheduledReportPageComponent),
          add('my-report-page', MyReportPageComponent),
          add('new-report-page', EditReportPageComponent),
          add('edit-report-page', EditReportPageComponent),
          add('new-filter-page', EditFilterPageComponent),
          add('edit-filter-page', EditFilterPageComponent),
          add('pending-report-page', PendingReportPageComponent),
          add('edit-schedule-page', EditScheduleComponent),
        ],
      },
      {
        path: 'alerts',
        component: AlertsLayoutComponent,
        canActivate: [pageAuthorize('alerts-page')],
        children: [
          add('alerts-landing-page', AlertsLandingComponent, {
            routeCacheKey: 'AlertsLandingComponent',
          }),
          add('edit-alert-page', AlertEditPageComponent),
        ],
      },
      {
        path: 'query',
        component: QueryLayoutComponent,
        canActivate: [pageAuthorize('query-page')],
        children: [
          {
            path: '',
            component: QueryPageComponent,
            title: prefix + 'Query',
            data: { routeCacheKey: 'QueryPageComponent' },
          },
        ],
      },
      add('dashboards-page', DashboardsLayoutComponent),
      {
        path: '',
        component: PageLayoutComponent,
        children: [
          add('home-page', HomePageComponent, {
            routeCacheKey: 'HomePageComponent',
          }),
          add('design-page', DesignPageComponent),
          add('todo-page', TodoViewComponent, {
            routeCacheKey: 'TodoViewComponent',
          }),
          add('diary-types-page', DiaryTypesPageComponent),
          add('diary-edit-page', DiaryEditPageComponent),
          add('my-tasks-page', MyTasksComponent),
          add('task-edit-page', TaskEditPageComponent),
          add('applications-page', ApplicationsPageComponent),
          add('preferences-page', PreferencesComponent),
          add(
            'communication-preferences-page',
            CommunicationPreferencesComponent,
          ),
          add('password-change-page', PasswordChangeComponent),
          add('downloads-page', DownloadsViewComponent),
          {
            path: 'qa-tools',
            component: QaToolsComponent,
            title: prefix + 'QA Tools',
          },
        ],
      },

      {
        path: 'view',
        title: prefix + 'View',
        component: ViewLandingComponent,
        data: {
          routeCacheKey: 'ViewLandingComponent',
        },
      },
      {
        path: 'view/claim/:recordSource/:recordId',
        component: ClaimLayoutComponent,
        resolve: { claim: ClaimResolver },
        children: [
          add('claim-summary-page', ClaimSummaryComponent),
          add('claimant-page', ClaimantComponent),
          add('legal-page', LegalComponent),
          add('address-book-page', AddressBookComponent),
          add('appeals-page', AppealsComponent),
          add('disability-page', DisabilityComponent),
          add('general-liability-page', GeneralLiabilityComponent),
          add('workers-compensation-page', WorkersCompensationComponent),
          add('property-page', PropertyComponent),
          add('auto-liability-page', AutoComponent),
          add('medical-page', MedicalComponent),
          add('notes-page', NotesComponent),
          add('note-edit-page', NoteEditPageComponent),
          add('documents-page', DocumentsComponent),
          add('correspondence-page', CorrespondenceComponent),
          add('upload-page', UploadDocumentsComponent),
          add('custom-fields-page', CustomFieldsPageComponent),
          add('examiner-diary-page', ExaminerDiaryComponent),
          add('document-tasks-page', TodoViewComponent),
          add('document-task-edit-page', TaskEditPageComponent),
          add('time-tracking-page', TimeTrackingComponent),
          add('earnings-page', EarningsComponent),
          add('accruals-page', AccrualsComponent),
          add('deductions-page', DeductionsComponent),
          add('hr-contacts-page', HrContactsComponent),
          add('dependents-page', DependentsComponent),
          add('payroll-page', PayrollComponent),
          add('employee-detail-page', EmployeeDetailComponent),
          add('job-history-page', JobHistoryComponent),
          add('benefit-plan-page', BenefitPlanComponent),
          add('overview-page', OverviewComponent),
          add('payment-history-page', PaymentHistoryComponent),
          add('recoveries-page', RecoveriesComponent),
          add('reserves-page', ReservesComponent),
          add('leave-calendar-page', LeaveCalendarPageComponent, null, {
            absences: LeaveCalendarResolver,
          }),

          add('reimbursements-page', ReimbursementsComponent),
          add('overpayments-page', OverpaymentsComponent),
          add(
            'overpayment-detail/:overpaymentId-page',
            OverpaymentDetailComponent,
          ),
          add('offsets-page', OffsetsComponent),
          add('invoice-list-page', InvoiceListComponent),
          add('invoice-bill/:icn-page', InvoiceBillComponent),
          add('absence-page', AbsenceComponent),
          add('eligibility-page', EligibilityComponent),
          add('certifications-page', CertificationsComponent),
          add('index-system-page', IndexSystemPageComponent),
          add('tender-page', TenderComponent),
          add('plan-summary-page', PlanSummaryPageComponent),
          add('job-functions-page', JobFunctionsComponent),
          add('interactions-page', InteractionsComponent),
          add('restrictions-page', RestrictionsComponent),
          add('accommodations-page', AccommodationsComponent),
          add('milestones-page', MilestonesComponent),
          add('edi-page', ElectronicDataInterchangeComponent),
          add('policy-details-page', PolicyDetailPageComponent),
          add('vendor-referral-page', VendorReferralComponent),
          add('case-management-page', CaseManagementComponent),
          add(
            'case-management-detail/:serviceId-page',
            CaseManagementDetailComponent,
          ),
          add('service-instructions-page', ServiceInstructionComponent),
          add('medicare-page', MedicareComponent),
          add('sidekick-summary-page', SidekickSummaryComponent),
        ],
      },
    ],
  },
  add('forbidden-page', ForbiddenPageComponent),
  add('not-found-page', NotFoundPageComponent),
  add('sso-error-page', SsoErrorPageComponent),
  add('empty-page', EmptyComponent),
  {
    path: '**',
    redirectTo: '/not-found',
  },
];

function add<T>(
  pageToken: PageToken,
  type: Type<T>,
  data: Data = null,
  resolve: ResolveData = null,
): Route {
  const settings = pageSettings.get(pageToken);
  return {
    path: settings.path,
    component: type,
    title: prefix + settings.title,
    canActivate: [pageAuthorize(pageToken)],
    data: data,
    resolve: resolve,
  };
}
